import type { ParsedQs } from 'qs';

type QsValue = string | ParsedQs | Array<string | ParsedQs> | undefined;

/**
 * Extracts the first string value from a qs parsed value.
 * Useful for single-value query params like `redirect`, `token`, `secret`.
 *
 * @param value - The parsed qs value
 * @returns The first string value, or undefined if not a string
 */
export function firstStringFromQueryString(value: QsValue): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    const first = value[0];
    if (typeof first === 'string') {
      return first;
    }
  }
  return undefined;
}

/**
 * Extracts a string array from a qs parsed value.
 * Useful for multi-value query params like filter arrays.
 *
 * @param value - The parsed qs value
 * @returns An array of strings, or undefined if no valid strings found
 */
export function stringArrayFromQueryString(value: QsValue): string[] | undefined {
  if (typeof value === 'string') {
    return [value];
  }
  if (Array.isArray(value)) {
    const strings = value.filter((v): v is string => typeof v === 'string');
    return strings.length > 0 ? strings : undefined;
  }
  return undefined;
}

/**
 * Extracts a number from a qs parsed value.
 * Useful for numeric query params like `limit`, `offset`.
 *
 * @param value - The parsed qs value
 * @param defaultValue - The default value to return if parsing fails
 * @returns The parsed number, or the default value
 */
export function numberFromQueryString(value: QsValue, defaultValue: number = 0): number {
  const str = firstStringFromQueryString(value);
  if (str === undefined) {
    return defaultValue;
  }
  const num = parseInt(str, 10);
  return isNaN(num) ? defaultValue : num;
}

/**
 * Extracts a boolean from a qs parsed value.
 * Treats 'true' as true, everything else as false or undefined.
 *
 * @param value - The parsed qs value
 * @returns true if value is 'true', false if value is 'false', undefined otherwise
 */
export function booleanFromQueryString(value: QsValue): boolean | undefined {
  const str = firstStringFromQueryString(value);
  if (str === 'true') {
    return true;
  }
  if (str === 'false') {
    return false;
  }
  return undefined;
}

/**
 * Extracts a string value that must match one of the allowed values (enum-like).
 * Useful for query params that should be one of a specific set of values.
 *
 * @param value - The parsed qs value
 * @param allowedValues - Array of allowed string values
 * @returns The string if it matches an allowed value, undefined otherwise
 */
export function enumFromQueryString<T extends string>(value: QsValue, allowedValues: readonly T[]): T | undefined {
  const str = firstStringFromQueryString(value);
  if (str !== undefined && (allowedValues as readonly string[]).includes(str)) {
    return str as T;
  }
  return undefined;
}
